import React, { useState, useEffect, useMemo } from 'react';
import { Form, Button, Badge, Modal, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import { BookA, Plus, Pencil, AlertTriangle, Archive, ArchiveRestore, ChevronDown, Copy, LockKeyhole } from 'lucide-react';
import InfoHint from '../SharedComponents/InfoHint';
import SettingsSelect from './SettingsSelect';
import axios from '../../../services/AxiosInstance';
import { getApiUrl } from '../../../utils/ApiUtils';
import { ListToolbar } from '../Common/ListToolbar';
import { toast } from 'sonner';
import { COMPANY_TYPE_TO_SHORT_CODE, toShortCode } from '../../../constants/companyTypes';
import { brandSelectTheme } from '../Common/reactSelectTheme';
import { OwnAccountChip } from '../../../utils/accountPickerOptions';
import { invalidateChartRoles } from '../../../services/api/chartRolesApi';
import { fetchVatMetadata, mappingOptions } from '../../../services/api/vatMetadataApi';

/** A locked field is never echoed back by an unrelated name/SRU edit. */
export function vatMappingEditPayload(account) {
    if (account.managed_vat?.locked) return account.clearManagedVatMapping ? { VAT_mapping: '' } : {};
    return { VAT_mapping: account.VAT_mapping || '' };
}

export function ManagedVatSetting({ account, onClear }) {
    return <div className="small p-2 bg-light rounded">
        <div className="fw-semibold"><LockKeyhole size={14} className="me-1" />Hanteras av LedgerLoop</div>
        <p className="my-2">Ingår inte i momsdeklarationen. Momsinställningen är låst för kontots ändamål: {account.managed_vat.purpose}.</p>
        <p className="text-muted mb-2">Ett ändrat visningsnamn ändrar inte kontots ändamål. Använd ett annat konto för en annan typ av händelse.</p>
        {account.VAT_mapping && <>
            <p>Det finns en tidigare koppling till fält {account.VAT_mapping} som behöver rättas. Redan låsta deklarationer ändras inte.</p>
            <Button size="sm" variant="outline-primary" onClick={onClear}>Ta bort den tidigare kopplingen</Button>
        </>}
        {account.clearManagedVatMapping && <p className="mb-0">Kopplingen tas bort när du väljer Spara.</p>}
    </div>;
}

// Canonical short codes for the accounts.applicable_company_types SET column.
// Order here drives the badge / checkbox order in the UI.
const COMPANY_TYPE_CODES = ['AB', 'EF', 'HB', 'KB', 'EK', 'IF', 'ST'];
const SHORT_CODE_TO_LABEL = Object.entries(COMPANY_TYPE_TO_SHORT_CODE).reduce(
    (acc, [label, code]) => { acc[code] = label; return acc; },
    {}
);

// Parse the SET column (e.g. "AB,EF,HB") into a sorted array.
// A null/missing value is treated as "applies to every company type" so legacy
// rows that pre-date migration 02 don't suddenly disappear from the view.
function parseApplicableTypes(raw) {
    if (raw === null || raw === undefined || raw === '') return [...COMPANY_TYPE_CODES];
    if (Array.isArray(raw)) return raw;
    return raw.split(',').map(s => s.trim()).filter(Boolean);
}

function isUniversalSet(codes) {
    return COMPANY_TYPE_CODES.every(c => codes.includes(c));
}

// The momsrapportkoder come from the backend register (G16). The list that
// used to live here was one of four copies and carried pre-ML-2023 wording —
// fält 41 read "Övrig försäljning utanför EU", which is a different
// transaction from "Försäljning när köparen är betalningsskyldig i Sverige".

/**
 * "Behöver åtgärd" (independent review 2026-09-05, F12; UX_PLAN.md P10) is the
 * RESOLVED reporting state the backend computes per row — the momsdeklaration's
 * own classification (`reporting_state.vat`) and the SRU form's coverage of
 * the number (`reporting_state.sru`) — never "two columns are NULL". An own
 * bank or cost account resolves through the form's intervals and needs
 * nothing; a revenue account with no momsrapportkod and no "ingår inte"
 * decision, or a number no line of the form covers, does. Archived rows are
 * never a to-do here (I6). Without a backend state nothing is flagged rather
 * than guessing.
 */
export function accountNeedsAttention(account) {
    if (!account || account.archived_at) return false;
    const state = account.reporting_state;
    if (!state) return false;
    return Boolean(state.needs_attention);
}

export function attentionTitle(account) {
    const state = account?.reporting_state || {};
    const parts = [];
    if (state.vat === 'needs_box') {
        parts.push('Momsbehandlingen behöver bekräftas — se underlag och förslag i Kontoplanshälsa eller momsdeklarationen');
    }
    if (state.vat === 'mapping_conflict') parts.push('Momsinställningen stämmer inte med bokningarnas identifierade betydelse — se Kontoplanshälsa');
    if (state.vat_scope) parts.push(`Momskontroll för ${state.vat_scope.start_date} – ${state.vat_scope.end_date}`);
    if (state.sru === 'uncovered') {
        parts.push(`Ingen rad i ${state.form || 'deklarationsblanketten'} täcker kontot — ange SRU-kod`);
    }
    if (state.sru === 'invalid_override') {
        parts.push(`SRU-koden finns inte i ${state.form || 'deklarationsblanketten'} — rätta den`);
    }
    if (state.sru === 'decision_required') {
        parts.push('Momsfält 42 kan höra till R1 eller R2 — välj verksamhetens deklarationsrad i NE-steget');
    }
    return parts.join('. ');
}

/** The chip's short text (UX_PLAN.md P10): what is missing, not a bare warning triangle. */
export function attentionLabel(account) {
    const state = account?.reporting_state || {};
    const parts = [];
    if (state.vat === 'needs_box') parts.push('Bekräfta momsbehandling');
    if (state.vat === 'mapping_conflict') parts.push('Kontrollera momsinställning');
    if (state.sru === 'uncovered') parts.push('Saknar deklarationsrad');
    if (state.sru === 'invalid_override') parts.push('Ogiltig SRU-kod');
    if (state.sru === 'decision_required') parts.push('Välj R1 eller R2');
    return parts.join(' · ');
}

function AccountPlanManager({ companyId, companyType }) {
    const tenantShortCode = useMemo(() => toShortCode(companyType), [companyType]);
    const tenantTypeLabel = companyType || 'detta bolag';

    const [accounts, setAccounts] = useState([]);
    const [vatMappingOptions, setVatMappingOptions] = useState([{ value: '', label: 'Ingen' }]);

    useEffect(() => {
        let cancelled = false;
        fetchVatMetadata(companyId)
            .then((metadata) => { if (!cancelled) setVatMappingOptions(mappingOptions(metadata)); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [companyId]);

    const vatLabelByCode = useMemo(() => Object.fromEntries(
        vatMappingOptions.filter((option) => option.value).map((option) => [option.value, option.label])
    ), [vatMappingOptions]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [planFilter, setPlanFilter] = useState('all');
    // Filter on by default — T05 / T06 / T07 expect non-applicable rows hidden
    // out of the box. Toggling off reveals every row plus its company-type
    // badges, which is the escape hatch for SIE-imported / mis-tagged accounts.
    const [onlyApplicable, setOnlyApplicable] = useState(true);
    // Archived rows are hidden from this list by default, exactly like the
    // pickers (I6) — the toggle reveals them for unarchiving/inspection.
    const [showArchived, setShowArchived] = useState(false);
    const [archivingId, setArchivingId] = useState(null);
    // Which kontoklasser the user has explicitly opened or closed.
    const [openClasses, setOpenClasses] = useState({});
    const [copySource, setCopySource] = useState(null);
    const [editingAccount, setEditingAccount] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [saving, setSaving] = useState(false);
    const [newAccount, setNewAccount] = useState({
        accountNumber: '', accountName: '', SRU: '', VAT_mapping: '', baseAccountId: ''
    });

    const fetchAccounts = async () => {
        try {
            const response = await axios.get(
                getApiUrl(`${companyId}/accounting/accounts/full`),
                { withCredentials: true }
            );
            if (response.data.success) {
                setAccounts(response.data.accounts);
            }
        } catch (error) {
            console.error('Error fetching accounts:', error);
            toast.error('Kunde inte hämta kontoplanen');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAccounts();
    }, [companyId]);

    const filteredAccounts = useMemo(() => {
        return accounts.filter(account => {
            if (!showArchived && account.archived_at) return false;
            if (planFilter === 'base' && !account.is_base_plan) return false;
            if (planFilter === 'custom' && account.is_base_plan) return false;
            if (planFilter === 'needs' && !accountNeedsAttention(account)) return false;
            if (onlyApplicable && tenantShortCode) {
                const codes = parseApplicableTypes(account.applicable_company_types);
                if (!codes.includes(tenantShortCode)) return false;
            }
            if (search) {
                const s = search.toLowerCase();
                return account.account_number.includes(s) ||
                    (account.account_name && account.account_name.toLowerCase().includes(s));
            }
            return true;
        });
    }, [accounts, search, planFilter, onlyApplicable, tenantShortCode, showArchived]);

    // BAS kontoklasser. Names verbatim from BAS-intressenternas förening,
    // "Kontoplanens uppbyggnad och användning"
    // (https://www.bas.se/kontoplaner/kontoplanens-uppbyggnad-och-anvandning/,
    // read 2026-09-03). Classes 5 and 6 share one name in BAS and are shown as
    // one group, which is how BAS itself presents them.
    const BAS_CLASSES = [
        { key: '1', match: ['1'], label: 'Tillgångar' },
        { key: '2', match: ['2'], label: 'Eget kapital och skulder' },
        { key: '3', match: ['3'], label: 'Inkomster/intäkter' },
        { key: '4', match: ['4'], label: 'Utgifter/kostnader för varor, material och vissa köpta tjänster' },
        { key: '5-6', match: ['5', '6'], label: 'Övriga externa rörelseutgifter/kostnader' },
        { key: '7', match: ['7'], label: 'Utgifter/kostnader för personal, avskrivningar mm' },
        { key: '8', match: ['8'], label: 'Finansiella och andra inkomster/intäkter och utgifter/kostnader samt skatte- och resultatkonton' },
    ];

    // 1 247 rows in one table was 26 884 DOM nodes and a visible stall on every
    // keystroke. Grouped by kontoklass only the class you opened is mounted —
    // and browsing a chart by class is how the chart is organised anyway.
    const accountGroups = useMemo(() => {
        const groups = BAS_CLASSES.map((c) => ({ ...c, accounts: [] }));
        const other = { key: 'övriga', match: [], label: 'Övriga konton', accounts: [] };
        filteredAccounts.forEach((account) => {
            const digit = String(account.account_number || '').charAt(0);
            const group = groups.find((g) => g.match.includes(digit)) || other;
            group.accounts.push(account);
        });
        return [...groups, other].filter((g) => g.accounts.length > 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredAccounts]);

    // Narrowing the list is a request to see what matched, so a search or a
    // filter opens every class. Unnarrowed, they start closed and the chart is
    // seven rows instead of twelve hundred.
    const narrowed = Boolean(search) || planFilter !== 'all';

    // Explicit open/closed choices belong to the list you made them on. Without
    // this, having collapsed kontoklass 1 while browsing meant a later search
    // for "1930" found the account, put it in class 1 — and left that class
    // shut, so the one hit was invisible.
    useEffect(() => {
        setOpenClasses({});
    }, [search, planFilter]);

    // A click within the current narrowing still wins.
    const isClassOpen = (key) => {
        if (Object.prototype.hasOwnProperty.call(openClasses, key)) return openClasses[key];
        return narrowed;
    };
    const toggleClass = (key) => {
        setOpenClasses((prev) => ({ ...prev, [key]: !isClassOpen(key) }));
    };

    const renderAccountRow = (account) => {
    const codes = parseApplicableTypes(account.applicable_company_types);
    const universal = isUniversalSet(codes);
    return (
    <tr key={account.account_id} style={{ opacity: account.archived_at ? 0.55 : (account.is_base_plan ? 0.85 : 1) }}>
        <td><strong>{account.account_number}</strong></td>
        <td>
            {account.account_name}
            {/* Import evidence (spec v3 I3): what the file called the number is
                shown, never adopted. Equal names are the created/adopted case
                and carry no hint. */}
            {account.import_source_name && account.import_source_name !== account.account_name && (
                <div
                    className="text-muted"
                    style={{ fontSize: '0.75rem' }}
                    title={account.import_source_system ? `Namnet i importfilen från ${account.import_source_system}` : 'Namnet i importfilen'}
                >
                    I importfilen: {account.import_source_name}
                </div>
            )}
        </td>
        <td><span className="text-muted">{account.SRU || 'Ingen'}</span></td>
        <td>
            {account.managed_vat?.locked ? <span className="small" title={account.managed_vat.explanation}>
                <LockKeyhole size={13} className="me-1" />Hanteras av LedgerLoop
                {account.VAT_mapping && <span className="d-block text-warning">Tidigare fält {account.VAT_mapping} behöver rättas</span>}
            </span> :
            <span
                className="text-muted"
                title={account.VAT_mapping ? (vatLabelByCode[account.VAT_mapping] || undefined) : undefined}
            >
                {account.VAT_mapping || 'Ingen'}
            </span>}
        </td>
        {!onlyApplicable && (
            <td>
                {universal ? (
                    <span className="text-muted" style={{ fontSize: '0.75rem' }}>alla företagsformer</span>
                ) : (
                    codes.map(code => (
                        <Badge
                            key={code}
                            bg={code === tenantShortCode ? 'primary' : 'secondary'}
                            className="me-1"
                            style={{ fontSize: '0.7rem' }}
                            title={SHORT_CODE_TO_LABEL[code] || code}
                        >
                            {code}
                        </Badge>
                    ))
                )}
            </td>
        )}
        <td>
            {!account.is_base_plan && (
                <span
                    title={account.import_source_system
                        ? `Eget konto, skapat av en import från ${account.import_source_system}`
                        : 'Eget konto'}
                >
                    <OwnAccountChip />
                </span>
            )}
            {accountNeedsAttention(account) && (
                <button
                    type="button"
                    className="btn btn-link p-0 ms-1 align-baseline"
                    style={{ fontSize: '0.7rem', fontWeight: 600, color: '#b45309', textDecoration: 'none' }}
                    title={attentionTitle(account)}
                    onClick={() => { setCopySource(null); setEditingAccount({ ...account }); }}
                >
                    <AlertTriangle size={12} className="me-1" aria-hidden="true" />
                    {attentionLabel(account)}
                </button>
            )}
            {Boolean(account.name_customized) && (
                <Badge bg="secondary" className="ms-1 me-1" style={{ fontSize: '0.7rem' }}
                    title="Namnet är medvetet ändrat och skyddas mot framtida namnsynk från kontoplanskatalogen">
                    Eget namn
                </Badge>
            )}
            {account.archived_at && (
                <Badge bg="dark" className="ms-1" style={{ fontSize: '0.7rem' }}
                    title="Döljs i kontoval; historik och rapporter påverkas inte">
                    Arkiverat
                </Badge>
            )}
        </td>
        <td style={{ whiteSpace: 'nowrap' }}>
            <Button
                variant="link"
                size="sm"
                className="p-0 me-2"
                onClick={() => { setCopySource(null); setEditingAccount({ ...account }); }}
            >
                <Pencil size={14} />
            </Button>
            <Button
                variant="link"
                size="sm"
                className="p-0"
                disabled={archivingId === account.account_id}
                title={account.archived_at ? 'Återställ kontot (visas i kontoval igen)' : 'Arkivera kontot (döljs i kontoval; historik behålls)'}
                onClick={() => handleSetArchived(account, !account.archived_at)}
            >
                {account.archived_at
                    ? <ArchiveRestore size={14} />
                    : <Archive size={14} className="text-muted" />}
            </Button>
        </td>
    </tr>
    );
    };

    const handleSaveEdit = async () => {
        const editedTypes = parseApplicableTypes(editingAccount.applicable_company_types);
        if (editedTypes.length === 0) {
            toast.error('Minst en bolagstyp måste vara markerad');
            return;
        }
        setSaving(true);
        try {
            const response = await axios.put(
                getApiUrl(`${companyId}/accounting/accounts/${editingAccount.account_id}`),
                {
                    accountName: editingAccount.account_name,
                    SRU: editingAccount.SRU || '',
                    ...vatMappingEditPayload(editingAccount),
                    baseAccountId: editingAccount.base_account_id || '',
                    applicableCompanyTypes: editedTypes
                },
                { withCredentials: true }
            );
            if (response.data.success) {
                toast.success('Kontot har uppdaterats');
                setEditingAccount(null);
                // Names/metadata feed the cached role resolutions.
                invalidateChartRoles(companyId);
                fetchAccounts();
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Kunde inte uppdatera kontot');
        } finally {
            setSaving(false);
        }
    };

    const toggleEditingType = (code) => {
        const current = parseApplicableTypes(editingAccount.applicable_company_types);
        const next = current.includes(code)
            ? current.filter(c => c !== code)
            : [...current, code];
        setEditingAccount({ ...editingAccount, applicable_company_types: next.join(',') });
    };

    const handleCreateAccount = async () => {
        setSaving(true);
        try {
            const response = await axios.post(
                getApiUrl(`${companyId}/accounting/accounts`),
                newAccount,
                { withCredentials: true }
            );
            if (response.data.success) {
                toast.success('Kontot har skapats');
                invalidateChartRoles(companyId);
                setShowAddModal(false);
                setNewAccount({ accountNumber: '', accountName: '', SRU: '', VAT_mapping: '', baseAccountId: '' });
                fetchAccounts();
            }
        } catch (error) {
            const msg = error.response?.data?.message || 'Kunde inte skapa kontot';
            toast.error(msg);
        } finally {
            setSaving(false);
        }
    };

    const baseCount = accounts.filter(a => a.is_base_plan && !a.archived_at).length;
    const customCount = accounts.filter(a => !a.is_base_plan).length;
    const needsCount = accounts.filter(accountNeedsAttention).length;
    const archivedCount = accounts.filter(a => a.archived_at).length;
    // The chart the standard rows come from, as the rows themselves record it.
    const chartVersionCode = accounts.find((a) => a.is_base_plan && a.chart_version_code)?.chart_version_code || null;
    const chartVersionLabel = chartVersionCode ? chartVersionCode.replace(/^BAS(\d{4})$/, 'BAS $1') : null;

    // Archive/unarchive — picker/suggestion visibility only; history and
    // reports keep archived accounts wherever they have activity (I6).
    const handleSetArchived = async (account, archived) => {
        setArchivingId(account.account_id);
        try {
            await axios.put(
                getApiUrl(`${companyId}/accounting/chart/accounts/${account.account_number}/archive`),
                { archived },
                { withCredentials: true }
            );
            toast.success(archived
                ? `Konto ${account.account_number} arkiverat — det döljs i kontoval men behålls i historik och rapporter.`
                : `Konto ${account.account_number} återställt.`);
            // Archiving changes role resolvability (an archived target makes
            // a role needs-mapping) — drop the cached roles (review P2).
            invalidateChartRoles(companyId);
            fetchAccounts();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Arkiveringen kunde inte sparas');
        } finally {
            setArchivingId(null);
        }
    };

    if (loading) {
        return (
            <div className="modern-card">
                <div className="modern-card-body text-center py-5">
                    <Spinner animation="border" className="text-primary" />
                    <p className="text-muted mt-2">Laddar kontoplan...</p>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="modern-card">
                <div className="modern-card-header">
                    <div>
                        <h2 className="section-title">
                            <BookA size={16} />
                            Kontoplan
                        </h2>
                        <p className="section-subtitle">
                            {[
                                chartVersionLabel,
                                `${accounts.length} konton`,
                                customCount > 0 ? `${customCount} egna` : null,
                                archivedCount > 0 ? `${archivedCount} arkiverade` : null,
                            ].filter(Boolean).join(' · ')}
                        </p>
                    </div>
                    <Button variant="primary" size="sm" onClick={() => setShowAddModal(true)}>
                        <Plus size={16} className="me-1" />
                        Lägg till konto
                    </Button>
                </div>

                <div className="modern-card-body">
                    {/* Same filter idiom as Inkorg, Verifikat and the other
                        list pages: ListToolbar for search, FilterChips with
                        live counts for the set you are looking at. This was a
                        react-bootstrap ButtonGroup with Badges — the last
                        place in the app still filtering that way. */}
                    <ListToolbar
                        search={{
                            value: search,
                            onChange: setSearch,
                            placeholder: 'Sök konto…',
                        }}
                    />

                    {/* One row, two axes. The first four are an exclusive
                        set; the last two are toggles that combine with
                        whichever of those is active — hence aria-pressed and
                        the divider, so the row does not read as one group. */}
                    <div className="ll-chips mb-3" role="group" aria-label="Filtrera kontoplanen">
                        {[
                            { key: 'all', label: 'Alla', count: accounts.length },
                            { key: 'base', label: 'Standardkonton', count: baseCount },
                            { key: 'custom', label: 'Egna konton', count: customCount },
                            { key: 'needs', label: 'Behöver åtgärd', count: needsCount },
                        ].map((chip) => (
                            <button
                                key={chip.key}
                                type="button"
                                className={`ll-chip ${planFilter === chip.key ? 'active' : ''}`}
                                onClick={() => setPlanFilter(chip.key)}
                                aria-pressed={planFilter === chip.key}
                            >
                                {chip.label}
                                <span className="ll-chip__count">{chip.count}</span>
                            </button>
                        ))}

                        <span className="ll-chips__divider" aria-hidden="true" />

                        <button
                            type="button"
                            className={`ll-chip ll-chip--toggle ${showArchived ? 'active' : ''}`}
                            onClick={() => setShowArchived((v) => !v)}
                            aria-pressed={showArchived}
                            title="Arkiverade konton döljs i kontoval men behålls i historik och rapporter"
                        >
                            Arkiverade
                            <span className="ll-chip__count">{archivedCount}</span>
                        </button>
                        {/* UX_PLAN.md P10: the company-form filter is on by
                            default and reads as an escape hatch, not as "Bara
                            Enskild firma" — a phrase a first-time user cannot
                            place. */}
                        {tenantShortCode && (
                            <button
                                type="button"
                                className={`ll-chip ll-chip--toggle ${!onlyApplicable ? 'active' : ''}`}
                                onClick={() => setOnlyApplicable((v) => !v)}
                                aria-pressed={!onlyApplicable}
                                title={`Kontoplanen visar de konton som gäller för ${tenantTypeLabel}. Slå på för att se och redigera konton för andra företagsformer, t.ex. importerade konton med fel märkning.`}
                            >
                                Visa konton för andra företagsformer
                            </button>
                        )}
                    </div>

            <style>{`
                .account-table-scroll {
                    max-height: 600px;
                    overflow-y: auto;
                    border: 1px solid #e5e7eb;
                    border-radius: 6px;
                }
                .account-table-scroll table {
                    width: 100%;
                    border-collapse: collapse;
                    font-size: 0.875rem;
                    margin-bottom: 0;
                }
                .account-table-scroll th {
                    background: #f8fafc;
                    position: sticky;
                    top: 0;
                    z-index: 10;
                    padding: 0.6rem 0.75rem;
                    border-bottom: 2px solid #e5e7eb;
                    font-weight: 600;
                    color: #1e293b;
                    font-size: 0.8rem;
                }
                .account-table-scroll td {
                    padding: 0.4rem 0.75rem;
                    border-bottom: 1px solid #f1f5f9;
                    vertical-align: middle;
                }
                .account-table-scroll tr:hover td {
                    background-color: #f8fafc;
                }
            `}</style>
            <div className="account-table-scroll">
                <table>
                    <thead>
                                <tr>
                                    <th style={{ width: '80px' }}>Konto</th>
                                    <th>Kontonamn</th>
                                    <th style={{ width: '100px' }}>SRU-kod</th>
                                    <th style={{ width: '130px' }}>Momsrapportkod</th>
                                    {!onlyApplicable && <th style={{ width: '180px' }}>Gäller för</th>}
                                    <th style={{ width: '160px' }}>Status</th>
                                    <th style={{ width: '70px' }}></th>
                                </tr>
                            </thead>
                            {accountGroups.map((group) => {
                                const open = isClassOpen(group.key);
                                return (
                                    <tbody key={group.key}>
                                        <tr className="account-class-row">
                                            <td colSpan={onlyApplicable ? 6 : 7}>
                                                <button
                                                    type="button"
                                                    className={`account-class-toggle${open ? ' is-open' : ''}`}
                                                    onClick={() => toggleClass(group.key)}
                                                    aria-expanded={open}
                                                >
                                                    <ChevronDown size={14} className="account-class-chevron" aria-hidden="true" />
                                                    <span className="account-class-number">{group.key}</span>
                                                    <span className="account-class-label">{group.label}</span>
                                                    <span className="account-class-count">{group.accounts.length}</span>
                                                </button>
                                            </td>
                                        </tr>
                                        {open && group.accounts.map(renderAccountRow)}
                                    </tbody>
                                );
                            })}
                            <tbody>
                                {filteredAccounts.length === 0 && (
                                    <tr>
                                        <td colSpan={onlyApplicable ? 6 : 7} className="text-center text-muted py-4">
                                            Inga konton matchar sökningen
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                </table>
                    </div>
                </div>
            </div>

            {/* Edit Modal */}
            <Modal show={!!editingAccount} onHide={() => { setEditingAccount(null); setCopySource(null); }} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Redigera konto {editingAccount?.account_number}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {editingAccount && (
                        <Form>
                            <Form.Group className="mb-3">
                                <Form.Label>
                                    Kontonamn
                                    {Boolean(editingAccount.is_base_plan) && (
                                        <InfoHint text={Boolean(editingAccount.name_customized)
                                            ? 'Namnet är eget och skyddas mot framtida namnsynk från kontoplanskatalogen.'
                                            : 'Ändrar du namnet markeras det som eget och skyddas mot framtida namnsynk.'} />
                                    )}
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    value={editingAccount.account_name || ''}
                                    onChange={e => setEditingAccount({ ...editingAccount, account_name: e.target.value })}
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Momsrapportkod</Form.Label>
                                {editingAccount.managed_vat?.locked
                                    ? <ManagedVatSetting account={editingAccount} onClear={() => setEditingAccount({
                                        ...editingAccount, VAT_mapping: '', clearManagedVatMapping: true,
                                    })} />
                                    : <><SettingsSelect
                                    options={vatMappingOptions}
                                    value={editingAccount.VAT_mapping || ''}
                                    onChange={value => setEditingAccount({ ...editingAccount, VAT_mapping: value })}
                                />
                                <Form.Text className="text-muted">
                                    Fältet i momsdeklarationen där kontots belopp redovisas. Konton utanför momsen lämnas på ”Ingen”.
                                </Form.Text>
                                </>}
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>SRU-kod</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={editingAccount.SRU || ''}
                                    onChange={e => setEditingAccount({ ...editingAccount, SRU: e.target.value })}
                                    placeholder="Behövs normalt inte"
                                />
                                <Form.Text className="text-muted">
                                    Behövs normalt inte — deklarationsraden följer av kontonumret. Ange bara om Kontoplanshälsa
                                    visar att kontot saknar rad.
                                </Form.Text>
                            </Form.Group>
                            {!editingAccount.is_base_plan && !editingAccount.managed_vat?.locked && (
                                <Form.Group className="mb-3">
                                    <Form.Label className="d-flex align-items-center gap-1">
                                        <Copy size={14} /> Kopiera inställningar från konto
                                        <InfoHint text="Hämta momsrapportkod (och eventuell SRU-kod) från ett standardkonto med samma innebörd — t.ex. för ett importerat konto." />
                                    </Form.Label>
                                    <Select
                                        options={accounts
                                            .filter(a => a.account_id !== editingAccount.account_id && !a.archived_at)
                                            .map(a => ({
                                                value: a.account_id,
                                                label: `${a.account_number} — ${a.account_name || ''}`,
                                                account: a,
                                            }))}
                                        value={copySource}
                                        onChange={setCopySource}
                                        theme={brandSelectTheme}
                                        placeholder="Välj referenskonto…"
                                        isSearchable
                                        isClearable
                                        menuPlacement="auto"
                                    />
                                    {copySource && (
                                        <div className="mt-2 p-2" style={{ background: '#f8fafc', borderRadius: 6, fontSize: '0.85rem' }}>
                                            {/* I7: the values are SHOWN and adopted only by the explicit
                                                button press below, then saved by the ordinary Spara. */}
                                            <div>
                                                Momsrapportkod: <strong>{copySource.account.VAT_mapping
                                                    ? (vatLabelByCode[copySource.account.VAT_mapping] || copySource.account.VAT_mapping)
                                                    : 'ingen'}</strong>
                                            </div>
                                            <div>
                                                SRU-kod: <strong>{copySource.account.SRU || 'ingen egen kod'}</strong>
                                                {!copySource.account.SRU && (
                                                    <span className="text-muted"> (SRU löses normalt via kontonummerintervall — bara konton utanför intervallen behöver en egen kod)</span>
                                                )}
                                            </div>
                                            <Button
                                                size="sm"
                                                variant="outline-primary"
                                                className="mt-2"
                                                onClick={() => {
                                                    setEditingAccount({
                                                        ...editingAccount,
                                                        VAT_mapping: copySource.account.VAT_mapping || '',
                                                        SRU: copySource.account.SRU || '',
                                                    });
                                                    toast.info('Värdena är ifyllda — granska och tryck Spara för att bekräfta.');
                                                }}
                                            >
                                                Använd dessa värden
                                            </Button>
                                        </div>
                                    )}
                                </Form.Group>
                            )}
                            {/* UX_PLAN.md P10: seven company-form boxes are a
                                power-user setting; folded so a first-time user
                                meets name, momsrapportkod and SRU only. */}
                            <details className="mb-3">
                                <summary style={{ cursor: 'pointer', fontSize: '0.875rem', color: 'var(--secondary, #717579)' }}>
                                    Avancerat: gäller för företagsformer
                                </summary>
                                <Form.Group className="mt-2">
                                    <Form.Label>
                                        Gäller för företagsformer
                                        <InfoHint text="Styr om kontot visas i kontoplanen för respektive företagsform. Påverkar även SIE-export och momsdeklaration." />
                                    </Form.Label>
                                    <div className="d-flex flex-wrap gap-3">
                                        {COMPANY_TYPE_CODES.map(code => {
                                            const checked = parseApplicableTypes(editingAccount.applicable_company_types).includes(code);
                                            return (
                                                <Form.Check
                                                    key={code}
                                                    type="checkbox"
                                                    id={`applicable-${code}`}
                                                    checked={checked}
                                                    onChange={() => toggleEditingType(code)}
                                                    label={`${code} — ${SHORT_CODE_TO_LABEL[code] || code}`}
                                                />
                                            );
                                        })}
                                    </div>
                                </Form.Group>
                            </details>
                        </Form>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => { setEditingAccount(null); setCopySource(null); }}>Avbryt</Button>
                    <Button
                        variant="primary"
                        onClick={handleSaveEdit}
                        disabled={
                            saving ||
                            !editingAccount ||
                            parseApplicableTypes(editingAccount.applicable_company_types).length === 0
                        }
                    >
                        {saving ? <Spinner animation="border" size="sm" /> : 'Spara'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Add Modal */}
            <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Lägg till konto</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Kontonummer</Form.Label>
                            <Form.Control
                                type="text"
                                maxLength={4}
                                value={newAccount.accountNumber}
                                onChange={e => setNewAccount({ ...newAccount, accountNumber: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                                placeholder="T.ex. 3042"
                                isInvalid={newAccount.accountNumber.length > 0 && newAccount.accountNumber.length < 4}
                            />
                            <Form.Control.Feedback type="invalid">Måste vara exakt 4 siffror</Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Kontonamn</Form.Label>
                            <Form.Control
                                type="text"
                                value={newAccount.accountName}
                                onChange={e => setNewAccount({ ...newAccount, accountName: e.target.value })}
                                placeholder="T.ex. Försäljn tjänst 12% sv"
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Momsrapportkod</Form.Label>
                            <SettingsSelect
                                options={vatMappingOptions}
                                value={newAccount.VAT_mapping}
                                onChange={value => setNewAccount({ ...newAccount, VAT_mapping: value })}
                            />
                            <Form.Text className="text-muted">
                                Fältet i momsdeklarationen där kontots belopp redovisas. Konton utanför momsen lämnas på ”Ingen”.
                            </Form.Text>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>SRU-kod</Form.Label>
                            <Form.Control
                                type="text"
                                value={newAccount.SRU}
                                onChange={e => setNewAccount({ ...newAccount, SRU: e.target.value })}
                                placeholder="Behövs normalt inte"
                            />
                            <Form.Text className="text-muted">
                                Behövs normalt inte — deklarationsraden följer av kontonumret.
                            </Form.Text>
                        </Form.Group>
                        <p className="text-muted mb-0" style={{ fontSize: '0.8125rem' }}>
                            Kontot skapas som ett eget konto och visas direkt i kontovalet när du bokför.
                        </p>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowAddModal(false)}>Avbryt</Button>
                    <Button
                        variant="primary"
                        onClick={handleCreateAccount}
                        disabled={saving || newAccount.accountNumber.length !== 4 || !newAccount.accountName.trim()}
                    >
                        {saving ? <Spinner animation="border" size="sm" /> : 'Skapa konto'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}

export default AccountPlanManager;
